import { Icon, IconButton } from '@cbrebuild/blocks';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Percent from '../../../nucleus/formats/percent';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import { BROKER_TYPES } from '../../../services/voucher/commissions-service';
import formatAsCurrency from '../../../utils/format-as-currency';
import RequiredLabeledItem from './RequiredLabeledItem';
import { useRegisterRequiredRows, useRegisterRequiredField, useRegisterAddRowAction } from './RequiredFieldsContext';
import {
  selectCommissionsGrossCommissionTotal,
  selectTotalCommissionsForCommissions,
} from '../../../redux/selectors/voucher-selectors';

const amountMissing = commission => (
  !Number(commission.gross_commission) && !Number(commission.voucher_gross_commission_percent)
);

// Shared by each subtype's row registration below and the "at least one commission" checks
// further down, so both agree on what counts as a fully filled-out commission.
const isBrokerCommissionMissing = commission => !commission.broker || !commission.voucher_broker_rep_role
  || amountMissing(commission) || !commission.producer_role;
const isOfficeCommissionMissing = commission => !commission.voucher_office_allocation_name || !commission.deptid
  || amountMissing(commission) || !commission.producer_role;
const isPoolCommissionMissing = commission => !commission.voucher_cbre_pool_name || amountMissing(commission) || !commission.producer_role;

const CommissionsReview = () => {
  const {
    commissions, officeCommissions, poolCommissions, voucher,
    deal_type, dealId, salesforce_id, market_name, deal_life_cycle_stage, voucher_submission_status,
    cbreGrossCommission, netDeduction, cbre_commission_is_percent,
  } = useSelector(state => ({
    commissions: state.commissions.commissions.filter(({ cbre_broker_type }) => cbre_broker_type === BROKER_TYPES.EMPLOYEE),
    officeCommissions: state.commissions.commissions.filter(({ cbre_broker_type }) => cbre_broker_type === BROKER_TYPES.OFFICE),
    poolCommissions: state.commissions.commissions.filter(({ cbre_broker_type }) => cbre_broker_type === BROKER_TYPES.POOL),
    voucher: state.voucher.voucher,
    deal_type: state.deal.deal?.deal_type,
    dealId: state.deal.deal?.id,
    salesforce_id: state.deal.deal?.salesforce_id || '',
    market_name: state.deal.deal?.market_name || '',
    deal_life_cycle_stage: state.deal.deal?.deal_life_cycle_stage || '',
    voucher_submission_status: state.deal.deal?.voucher_submission_status || '',
    cbreGrossCommission: selectCommissionsGrossCommissionTotal(state),
    netDeduction: selectTotalCommissionsForCommissions(state),
    cbre_commission_is_percent: !!state.voucher.voucher?.cbre_commission_is_percent,
  }));
  const history = useHistory();
  const url = VOUCHER_ROUTES[2].routes[2].path.replace(':id', voucher.deal);
  const requiredText = () => {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
    return (<span className="voucher-error-text">Required</span>);
  };
  const renderCommissionAmount = commission => (
    Number(commission.gross_commission) || Number(commission.voucher_gross_commission_percent)
      ? formatAsCurrency(commission.gross_commission)
      : requiredText()
  );

  const isPursuitIdNotRequired = (
    ['Voucher Submitted', 'Voucher Resubmitted', 'Voucher Completed'].includes(voucher_submission_status) &&
    ['closed', 'Closed'].includes(deal_life_cycle_stage)
  );
  const isPursuitIdRequired = !isPursuitIdNotRequired &&
    deal_type === 'lease' &&
    ((cbreGrossCommission || 0) >= 100000 || (netDeduction || 0) >= 100000);
  const pursuitIdMissing = isPursuitIdRequired && !salesforce_id;
  const pursuitIdFormatInvalid = !!salesforce_id && !/^SF-\d{10}$/.test(salesforce_id);

  // Market name suffix from PR 689342 (CB-3505835) -- kept even when the format is invalid so
  // RequiredLabeledItem's `invalid` handling below still shows the user's entered value.
  const pursuitIdDisplay = salesforce_id ? `${salesforce_id}${market_name ? ` (${market_name})` : ''}` : '';

  if (pursuitIdFormatInvalid) localStorage.setItem('voucher_valid', JSON.stringify(false));

  if (pursuitIdMissing) localStorage.setItem('voucher_valid', JSON.stringify(false));

  const hasCompleteCommission = commissions.some(commission => !isBrokerCommissionMissing(commission))
    || officeCommissions.some(commission => !isOfficeCommissionMissing(commission))
    || poolCommissions.some(commission => !isPoolCommissionMissing(commission));
  const displayError = !voucher.is_early_invoice && !hasCompleteCommission;
  if (displayError) localStorage.setItem('voucher_valid', JSON.stringify(false));

  useRegisterRequiredRows(
    commissions,
    {
      getFieldId: commission => `commission-broker-${commission.id}`,
      isMissing: isBrokerCommissionMissing,
      buildEntry: commission => ({
        section: 'Commissions',
        label: commission.broker ? `${commission.broker.first_name} ${commission.broker.last_name}` : 'Broker Commission',
        customRowType: 'brokerCommission',
        rowGroup: 'broker',
        rowId: commission.id,
        rowProps: { deal_type, voucherId: voucher.id, cbre_commission_is_percent },
      }),
    },
    [commissions, deal_type, voucher.id, cbre_commission_is_percent],
  );

  useRegisterRequiredRows(
    officeCommissions,
    {
      getFieldId: commission => `commission-office-${commission.id}`,
      isMissing: isOfficeCommissionMissing,
      buildEntry: commission => ({
        section: 'Commissions',
        label: commission.voucher_office_allocation_name || 'Office Commission',
        customRowType: 'officeCommission',
        rowGroup: 'office',
        rowId: commission.id,
        rowProps: { dealId, voucherId: voucher.id, cbre_commission_is_percent },
      }),
    },
    [officeCommissions, dealId, voucher.id, cbre_commission_is_percent],
  );

  useRegisterRequiredRows(
    poolCommissions,
    {
      getFieldId: commission => `commission-pool-${commission.id}`,
      isMissing: isPoolCommissionMissing,
      buildEntry: commission => ({
        section: 'Commissions',
        label: commission.voucher_cbre_pool_name || 'Pool Commission',
        customRowType: 'poolCommission',
        rowGroup: 'pool',
        rowId: commission.id,
        rowProps: { dealId, voucherId: voucher.id, cbre_commission_is_percent },
      }),
    },
    [poolCommissions, dealId, voucher.id, cbre_commission_is_percent],
  );

  // Registers the "You must add at least one commission" message into the "Before you
  // submit" panel once this voucher's combined commissions list has genuinely been empty
  // (standard `useRegisterRequiredField` "ever needed attention" latch/validation behavior).
  useRegisterRequiredField(
    'commission-list-required',
    {
      isReview: true,
      missing: displayError,
      buildEntry: () => ({
        section: 'Commissions',
        label: 'Commission',
        requiredMessage: `You must add at least one commission to this ${voucher.is_early_invoice ? 'early invoice' : 'voucher'}`,
        // Once any commission row exists (even incomplete), ITS OWN missing-row entry
        // (registered above via useRegisterRequiredRows) already represents this same gap in
        // the "N required fields" footer count -- stop double-counting this message too
        // (it still keeps showing/staying red until a row is complete, just excluded here).
        excludeFromCount: commissions.length > 0 || officeCommissions.length > 0 || poolCommissions.length > 0,
      }),
    },
    [displayError, voucher.is_early_invoice, commissions.length, officeCommissions.length, poolCommissions.length],
  );

  // Registers "Add Broker" / "Add Office" / "Add Pool Account" as 3 INDEPENDENT buttons into
  // the panel -- reusing the exact same `createCommission` add buttons
  // `BrokerCommissions.js`/`OfficeCommissions.js`/`PoolCommissions.js` each render on the
  // live Commissions tab. Freezes whether the "at least one commission (of ANY type)"
  // requirement was outstanding at the moment this component first mounted, so a user
  // adding several commissions in one sitting keeps seeing all 3 buttons, but switching
  // tabs/screens (a fresh mount) re-evaluates it and hides all 3 together (and the whole
  // Commissions section, once it has no other registered entries left) if a commission
  // already exists by then -- while shown, each is positioned directly below its OWN
  // subtype's rows via a shared `rowGroup`/`isAddButton` (see `orderSectionEntries` in
  // `RequiredFieldsSummary.js`), so the section reads Broker rows -> "Add Broker" -> Office
  // rows -> "Add Office" -> Pool rows -> "Add Pool Account", instead of one combined block
  // of 3 buttons.
  const initialDisplayErrorRef = useRef(null);
  if (initialDisplayErrorRef.current === null) {
    initialDisplayErrorRef.current = displayError;
  }
  useRegisterAddRowAction(
    'commission-broker-add-button',
    initialDisplayErrorRef.current,
    () => ({
      section: 'Commissions', customRowType: 'brokerAdd', rowGroup: 'broker', isAddButton: true, rowProps: { dealId },
    }),
    [dealId, initialDisplayErrorRef.current],
  );
  useRegisterAddRowAction(
    'commission-office-add-button',
    initialDisplayErrorRef.current,
    () => ({
      section: 'Commissions', customRowType: 'officeAdd', rowGroup: 'office', isAddButton: true, rowProps: { dealId },
    }),
    [dealId, initialDisplayErrorRef.current],
  );
  useRegisterAddRowAction(
    'commission-pool-add-button',
    initialDisplayErrorRef.current,
    () => ({
      section: 'Commissions', customRowType: 'poolAdd', rowGroup: 'pool', isAddButton: true, rowProps: { dealId },
    }),
    [dealId, initialDisplayErrorRef.current],
  );

  return (
    <div className="voucher-review-group commissions-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>Commissions</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        <div className="pursuit-id-review">
          <RequiredLabeledItem
            label="Pursuit ID"
            item={pursuitIdDisplay}
            notRequired={!isPursuitIdRequired}
            invalid={pursuitIdFormatInvalid}
            fieldId="commissions-pursuit-id"
            section="Commissions"
            controlType="text"
            editTarget={{ reduxAction: 'updateDeal', fieldPath: 'salesforce_id' }}
            isReview
            requiredMessage={pursuitIdFormatInvalid ? 'Pursuit ID must start with SF followed by hyphen and then 10 digits.' : null}
          />
        </div>
        {pursuitIdFormatInvalid && (
          <p className="voucher-error-text">Pursuit ID must start with SF followed by hyphen and then 10 digits.</p>
        )}
        <h3>Commissions</h3>
        {displayError
          && <div className="voucher-error-text">You must add at least one commission to this {voucher.is_early_invoice ? 'early invoice' : 'voucher'}</div>}
        {commissions.length ?
          <table>
            <colgroup>
              <col name="broker" />
              <col name="rep role" />
              <col name="allocation" />
              <col name="amount" />
              <col name="producer roler" />
              <col name="lead producer" />
            </colgroup>
            <tbody>
              <tr className="borderless-row">
                <th>Broker</th>
                <th>Rep Role</th>
                <th>Allocation</th>
                <th>Amount</th>
                <th>Producer Role</th>
                <th>Lead Producer</th>
              </tr>
              {commissions.map(commission => (
                <tr key={commission.id}>
                  <td>
                    {commission.broker ? (
                      <div className="person-component">
                        <div className="person-textbox">
                          <p className="primary-content">{commission.broker.first_name} {commission.broker.last_name}</p>
                          <p className="secondary-content">{commission.broker.work_email || '--'}</p>
                          {commission.broker.peoplesoft_id && <p className="secondary-content">ID: {commission.broker.peoplesoft_id}</p>}
                          {commission.broker.office_name && <p className="secondary-content">{commission.broker.office_name}</p>}
                        </div>
                      </div>
                    ) : requiredText()}
                  </td>
                  <td>{commission.voucher_broker_rep_role || requiredText()}</td>
                  <td><Percent value={Number(commission.voucher_gross_commission_percent)} emptyState="0%" /></td>
                  <td>{renderCommissionAmount(commission)}</td>
                  <td>{commission.producer_role ? commission.producer_role : requiredText()}</td>
                  <td>{commission.broker_is_lead && <Icon iconName="checkmark" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        : ''}
        {officeCommissions.length ?
          <table>
            <colgroup>
              <col name="office" />
              <col name="unit" />
              <col name="client name" />
              <col name="allocation" />
              <col name="amount" />
              <col name="producer role" />
              <col name="lead producer" />
            </colgroup>
            <tbody>
              <tr className="borderless-row">
                <th>Office</th>
                <th>Unit</th>
                <th>Client Name</th>
                <th>Allocation</th>
                <th>Amount</th>
                <th>Producer Role</th>
                <th>Lead Producer</th>
              </tr>
              {officeCommissions.map(officeCommission => (
                <tr key={officeCommission.id}>
                  <td>{officeCommission.voucher_office_allocation_name || requiredText()}</td>
                  <td>{officeCommission.deptid || requiredText()}</td>
                  <td>{officeCommission.voucher_office_client_name }</td>
                  <td><Percent value={Number(officeCommission.voucher_gross_commission_percent)} emptyState="0%" /></td>
                  <td>{renderCommissionAmount(officeCommission)}</td>
                  <td>{officeCommission.producer_role || requiredText()}</td>
                  <td>{officeCommission.broker_is_lead && <Icon iconName="checkmark" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        : ''}
        {poolCommissions.length ?
          <table>
            <colgroup>
              <col name="pool account" />
              <col />
              <col name="allocation" />
              <col name="amount" />
              <col name="producer role" />
              <col name="lead producer" />
            </colgroup>
            <tbody>
              <tr className="borderless-row">
                <th>Pool Account</th>
                <th />
                <th>Allocation</th>
                <th>Amount</th>
                <th>Producer Role</th>
                <th>Lead Producer</th>
              </tr>
              {poolCommissions.map(poolCommission => (
                <tr key={poolCommission.id}>
                  <td>{poolCommission.voucher_cbre_pool_name || requiredText()}</td>
                  <td />
                  <td><Percent value={Number(poolCommission.voucher_gross_commission_percent)} emptyState="0%" /></td>
                  <td>{renderCommissionAmount(poolCommission)}</td>
                  <td>{poolCommission.producer_role || requiredText()}</td>
                  <td>{poolCommission.broker_is_lead && <Icon iconName="checkmark" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        : ''}
      </div>
    </div>
  );
};

export default CommissionsReview;
